// Frontend Entry (Restored Full Navigation)
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import DashboardLayout from './components/DashboardLayout';

// Pages
import Login from './pages/Login';
import SSOLogin from './pages/SSOLogin';
import Overview from './pages/Overview';
import IntentMatrix from './pages/prime/IntentMatrix';
import IntentGalaxy from './pages/prime/IntentGalaxy';
import OmniTrendRadar from './pages/prime/OmniTrendRadar';
import TargetedInjection from './pages/prime/TargetedInjection';
import ProbeDispatch from './pages/prime/ProbeDispatch';
import PrimeSetupWizard from './pages/prime/PrimeSetupWizard';
import PrimeProjectList from './pages/prime/PrimeProjectList';
import EngineMonitor from './pages/EngineMonitor';
import BrandInsights from './pages/BrandInsights';
import CommerceMatrix from './pages/commerce/CommerceMatrix';
import CommerceAnalytics from './pages/commerce/CommerceAnalytics';
import CommerceDataHub from './pages/commerce/CommerceDataHub';

import Tasks from './pages/Tasks';
import SeedTasks from './pages/SeedTasks';
import Templates from './pages/Templates';
import Users from './pages/Users';
import Roles from './pages/Roles';
import Accounts from './pages/Accounts';
import NewProject from './pages/NewProject';
import ContentSelection from './pages/ContentSelection';
import GenerateConfig from './pages/GenerateConfig';
import GeneratedList from './pages/GeneratedList';
import ArticleEditor from './pages/ArticleEditor';


import Subscriptions from './pages/Subscriptions';
import DataPush from './pages/settings/DataPush';
import GeoTaskResults from './pages/GeoTaskResults';
import SeoTasks from './pages/seo/SeoTasks';
import Guide from './pages/Guide';
import ProfilePage from './pages/settings/ProfilePage';

// Admin Pages
import UserGroups from './pages/admin/UserGroups';
import Announcements from './pages/admin/Announcements';
import Logs from './pages/admin/Logs';
import IndustryLibraryConfig from './pages/settings/IndustryLibraryConfig';
import BrandCompetitorConfig from './pages/settings/BrandCompetitorConfig';
import EngineConfig from './pages/settings/EngineConfig';
import CostSettings from './pages/admin/CostSettings';
import ApiSettings from './pages/admin/ApiSettings';
import ExpansionTasks from './pages/admin/ExpansionTasks';


import ContentAssets from './pages/content/ContentAssets';
// Corpus Pages
import CorpusCollection from './pages/corpus/CorpusCollection';
import CorpusCrawl from './pages/corpus/CorpusCrawl';
import CorpusRepository from './pages/corpus/CorpusRepository';
import CorpusInsights from './pages/corpus/CorpusInsights';
import Reports from './pages/Reports';
import MentionRecords from './pages/MentionRecords';
import SensitiveWords from './pages/settings/SensitiveWords';
import MediaDomainDict from './pages/settings/MediaDomainDict';


// Content Management
import ArticleManagementPage from './pages/content/ArticleManagement';
import ArticleRecycleBin from './pages/content/ArticleRecycleBin';
import ImageManagementPage from './pages/content/ImageManagement';
import ImageRecycleBin from './pages/content/ImageRecycleBin';


import SeoOverview from './pages/seo/SeoOverview';
import SeoConfig from './pages/seo/SeoConfig';
import SeoBrandInsights from './pages/seo/SeoBrandInsights';
import SeoMediaInsights from './pages/seo/SeoMediaInsights';
import SeoEcology from './pages/seo/SeoEcology';
import SeoCompare from './pages/seo/SeoCompare';
import SeoDashboard from './pages/seo/SeoDashboard';
import SeoSubscriptions from './pages/seo/SeoSubscriptions';
import SeoTaskResults from './pages/seo/SeoTaskResults';

// Template Management
import StructureTemplatePage from './pages/templates/StructureTemplatePage';
import PersonaTemplatePage from './pages/templates/PersonaTemplate';
import TemplateAnalysisPage from './pages/templates/TemplateAnalysis';
import TemplateStatisticsPage from './pages/templates/TemplateStatistics';
import StyleRulesPage from './pages/templates/StyleRules';
import ExportPublishPage from './pages/content/ExportPublish';
import PublishEffectPage from './pages/content/PublishEffect';
import OpsDashboard from './pages/settings/OpsDashboard';

// Protected Route Wrapper
function PrivateRoute({ children, requiredRole }) {
  const { user, loading } = useAuth();
  
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-100 text-gray-500">正在加载应用...</div>;
  if (!user) return <Navigate to="/login" replace />;
  
  if (requiredRole && user.role !== requiredRole && user.role !== 'ADMIN') {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100 text-red-500">访问被拒绝: 需要 {requiredRole} 角色。</div>;
  }

  return <DashboardLayout>{children}</DashboardLayout>;
}

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/sso-login" element={<SSOLogin />} />
          
          <Route path="/" element={
            <PrivateRoute>
              <Overview />
            </PrivateRoute>
          } />
          
          <Route path="/engine-monitor" element={
            <PrivateRoute>
              <EngineMonitor />
            </PrivateRoute>
          } />

          
          {/* Commerce Attribution Routes */}
          <Route path="/commerce/matrix" element={<PrivateRoute><CommerceMatrix /></PrivateRoute>} />
          <Route path="/commerce/analytics" element={<PrivateRoute><CommerceAnalytics /></PrivateRoute>} />
          <Route path="/commerce/data-hub" element={<PrivateRoute><CommerceDataHub /></PrivateRoute>} />

          <Route path="/brand-insights" element={
            <PrivateRoute>
              <BrandInsights />
            </PrivateRoute>
          } />

          

          <Route path="/new-project" element={
            <PrivateRoute>
              <NewProject />
            </PrivateRoute>
          } />

          
          <Route path="/subscriptions" element={<PrivateRoute><Subscriptions /></PrivateRoute>} />
          <Route path="/settings/data-push" element={<PrivateRoute><DataPush /></PrivateRoute>} />
          <Route path="/guide" element={<PrivateRoute><Guide /></PrivateRoute>} />
          <Route path="/settings/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
          <Route path="/results" element={<PrivateRoute><GeoTaskResults /></PrivateRoute>} />
          <Route path="/seo/tasks" element={<PrivateRoute><SeoTasks system="seo" /></PrivateRoute>} />
          <Route path="/seo/results" element={<PrivateRoute><SeoTaskResults /></PrivateRoute>} />

          <Route path="/tasks" element={<PrivateRoute><Tasks /></PrivateRoute>} />

          <Route path="/seed-tasks" element={
            <PrivateRoute>
              <SeedTasks />
            </PrivateRoute>
          } />

          {/* Task Flow Routes */}
          <Route path="/tasks/:id/content" element={
            <PrivateRoute>
              <ContentSelection />
            </PrivateRoute>
          } />
          
          <Route path="/tasks/:id/config" element={
            <PrivateRoute>
              <GenerateConfig />
            </PrivateRoute>
          } />

          <Route path="/tasks/:id/results" element={
            <PrivateRoute>
              <GeneratedList />
            </PrivateRoute>
          } />

          <Route path="/tasks/:id/editor/:articleId" element={
            <PrivateRoute>
              <ArticleEditor />
            </PrivateRoute>
          } />

          
          {/* SEO Matrix Routes */}
          <Route path="/seo/overview" element={<PrivateRoute><SeoOverview /></PrivateRoute>} />
          <Route path="/seo/dashboard" element={<PrivateRoute><SeoDashboard /></PrivateRoute>} />
          <Route path="/seo/insights" element={<PrivateRoute><SeoBrandInsights /></PrivateRoute>} />
          <Route path="/seo/media" element={<PrivateRoute><SeoMediaInsights /></PrivateRoute>} />
          <Route path="/seo/ecology" element={<PrivateRoute><SeoEcology /></PrivateRoute>} />
          <Route path="/seo/compare" element={<PrivateRoute><SeoCompare /></PrivateRoute>} />
          <Route path="/seo/config" element={<PrivateRoute><SeoConfig /></PrivateRoute>} />
          <Route path="/seo/engines" element={<PrivateRoute><EngineConfig mode="seo" /></PrivateRoute>} />
          <Route path="/seo/subscriptions" element={<PrivateRoute><SeoSubscriptions /></PrivateRoute>} />
          <Route path="/seo/results" element={<PrivateRoute><SeoTaskResults /></PrivateRoute>} />

          {/* Old Templates */}
          <Route path="/templates" element={
            <PrivateRoute>
              <Templates />
            </PrivateRoute>
          } />

          
          {/* Corpus & Records Routes */}
          <Route path="/mention-records" element={<PrivateRoute><MentionRecords /></PrivateRoute>} />
          <Route path="/corpus/assets" element={<PrivateRoute><ContentAssets /></PrivateRoute>} />
          <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
          <Route path="/corpus/collection" element={<PrivateRoute><CorpusCollection /></PrivateRoute>} />
          <Route path="/corpus/crawl" element={<PrivateRoute><CorpusCrawl /></PrivateRoute>} />
          <Route path="/corpus/repository" element={<PrivateRoute><CorpusRepository /></PrivateRoute>} />
          <Route path="/corpus/insights" element={<PrivateRoute><CorpusInsights /></PrivateRoute>} />
          <Route path="/settings/sensitive" element={<PrivateRoute><SensitiveWords /></PrivateRoute>} />
          <Route path="/settings/source-dict" element={<PrivateRoute><MediaDomainDict /></PrivateRoute>} />


          {/* Content Management */}
          <Route path="/content/articles" element={<PrivateRoute><ArticleManagementPage /></PrivateRoute>} />
          <Route path="/content/article-recycle-bin" element={<PrivateRoute><ArticleRecycleBin /></PrivateRoute>} />
          <Route path="/content/images" element={<PrivateRoute><ImageManagementPage /></PrivateRoute>} />
          <Route path="/content/image-recycle-bin" element={<PrivateRoute><ImageRecycleBin /></PrivateRoute>} />
          <Route path="/content/export" element={<PrivateRoute><ExportPublishPage /></PrivateRoute>} />
          <Route path="/content/effect" element={<PrivateRoute><PublishEffectPage /></PrivateRoute>} />

          {/* New Template Management */}
          <Route path="/templates/structure" element={<PrivateRoute><StructureTemplatePage /></PrivateRoute>} />
          <Route path="/templates/persona" element={<PrivateRoute><PersonaTemplatePage /></PrivateRoute>} />
          <Route path="/templates/rules" element={<PrivateRoute><StyleRulesPage /></PrivateRoute>} />
          <Route path="/templates/analysis" element={<PrivateRoute><TemplateAnalysisPage /></PrivateRoute>} />
          <Route path="/templates/statistics" element={<PrivateRoute><TemplateStatisticsPage /></PrivateRoute>} />

          <Route path="/accounts" element={
            <PrivateRoute>
              <Accounts />
            </PrivateRoute>
          } />

          {/* System Management */}
          <Route path="/system/groups" element={
            <PrivateRoute requiredRole="ADMIN">
              <UserGroups />
            </PrivateRoute>
          } />

          <Route path="/system/users" element={
            <PrivateRoute requiredRole="ADMIN">
              <Users />
            </PrivateRoute>
          } />

          {/* GEO Config Routes */}
                    <Route path="/settings/industry-library" element={<PrivateRoute><IndustryLibraryConfig /></PrivateRoute>} />
          <Route path="/settings/brand-competitor" element={<PrivateRoute><BrandCompetitorConfig /></PrivateRoute>} />
          <Route path="/settings/personas" element={<PrivateRoute><BrandCompetitorConfig /></PrivateRoute>} />
          <Route path="/settings/brands" element={
            <PrivateRoute>
              <BrandCompetitorConfig />
            </PrivateRoute>
          } />

          <Route path="/settings/engines" element={
            <PrivateRoute>
              <EngineConfig />
            </PrivateRoute>
          } />

          <Route path="/system/roles" element={
            <PrivateRoute requiredRole="ADMIN">
              <Roles />
            </PrivateRoute>
          } />
          
          <Route path="/system/ops-dashboard" element={
            <PrivateRoute requiredRole="ADMIN">
              <OpsDashboard />
            </PrivateRoute>
          } />

          <Route path="/users" element={<Navigate to="/system/users" replace />} />

          <Route path="/system/announcements" element={
            <PrivateRoute requiredRole="ADMIN">
              <Announcements />
            </PrivateRoute>
          } />

          <Route path="/system/cost" element={
            <PrivateRoute requiredRole="ADMIN">
              <CostSettings />
            </PrivateRoute>
          } />

          <Route path="/system/expansion" element={<PrivateRoute requiredRole="ADMIN"><ExpansionTasks /></PrivateRoute>} />
          <Route path="/system/logs" element={
            <PrivateRoute requiredRole="ADMIN">
              <Logs />
            </PrivateRoute>
          } />

          <Route path="/system/api-config" element={
            <PrivateRoute requiredRole="ADMIN">
              <ApiSettings />
            </PrivateRoute>
          } />

           
          {/* PRIME Routes */}
                    <Route path="/prime/projects" element={<PrivateRoute><PrimeProjectList /></PrivateRoute>} />
          <Route path="/prime/wizard" element={<PrivateRoute><PrimeSetupWizard /></PrivateRoute>} />
          <Route path="/prime/radar" element={<PrivateRoute><OmniTrendRadar /></PrivateRoute>} />
          <Route path="/prime/matrix" element={<PrivateRoute><IntentMatrix /></PrivateRoute>} />
          <Route path="/prime/galaxy" element={<PrivateRoute><IntentGalaxy /></PrivateRoute>} />
          <Route path="/prime/injection" element={<PrivateRoute><TargetedInjection /></PrivateRoute>} />
          <Route path="/prime/dispatch" element={<PrivateRoute><ProbeDispatch /></PrivateRoute>} />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}
console.log('Cache Busting 2026-09-11 00:05');
console.log('Cache Busting 2026-09-11 00:45');
console.log('Cache Busting 2026-09-27 13:00');
